import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";

function formatCurrency(amount) {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);
}

export default function TariffCalculator() {
  const nav = useNavigate();
  const [categories, setCategories] = useState([]);
  const [countries, setCountries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [calculating, setCalculating] = useState(false);
  const [tab, setTab] = useState("calculator");
  
  // Calculator form
  const [form, setForm] = useState({
    origin: "CAN",
    destination: "USA",
    category: "automotive",
    declaredValue: 100000,
    isUsmcaCompliant: false
  });
  const [result, setResult] = useState(null);
  
  // Rates lookup
  const [rates, setRates] = useState([]);
  const [ratesFilter, setRatesFilter] = useState({ origin: "", destination: "" });
  
  // Impact analysis
  const [analysis, setAnalysis] = useState(null);

  useEffect(() => {
    fetch("/api/tariffs/categories")
      .then(r => r.json())
      .then(data => {
        setCategories(data.categories || []);
        setCountries(data.countries || []);
        setLoading(false);
      })
      .catch(() => setLoading(false));
  }, []);

  const calculate = async () => {
    setCalculating(true);
    try {
      const res = await fetch("/api/tariffs/calculate", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(form)
      });
      const data = await res.json();
      setResult(data.calculation);
    } catch (e) {
      alert("Error calculating: " + e.message);
    } finally {
      setCalculating(false);
    }
  };

  const loadRates = async () => {
    const params = new URLSearchParams();
    if (ratesFilter.origin) params.append("origin", ratesFilter.origin);
    if (ratesFilter.destination) params.append("destination", ratesFilter.destination);
    
    const res = await fetch(`/api/tariffs/rates?${params}`);
    const data = await res.json();
    setRates(data.rates || []);
  };

  const loadAnalysis = async () => {
    if (!form.origin || !form.destination) return;
    const res = await fetch(`/api/tariffs/impact-analysis?origin=${form.origin}&destination=${form.destination}&declaredValue=${form.declaredValue}`);
    const data = await res.json();
    setAnalysis(data);
  };

  useEffect(() => {
    if (tab === "rates") loadRates();
    if (tab === "impact") loadAnalysis();
  }, [tab, ratesFilter]);

  if (loading) return <div style={{ padding: 40, textAlign: "center" }}>Loading...</div>;

  return (
    <div style={{ maxWidth: 1200, margin: "0 auto", padding: 20 }}>
      <div style={{ marginBottom: 24 }}>
        <h1 style={{ margin: 0, fontSize: 28, fontWeight: 900 }}>🌎 Tariff Calculator</h1>
        <p style={{ margin: "8px 0 0 0", color: "#6b7280" }}>Cross-border tariff calculations with USMCA compliance</p>
      </div>

      {/* Tabs */}
      <div style={{ display: "flex", gap: 8, marginBottom: 24, borderBottom: "2px solid #e5e7eb", paddingBottom: 8 }}>
        {[
          { id: "calculator", label: "🧮 Calculator" },
          { id: "rates", label: "📊 Rate Lookup" },
          { id: "impact", label: "📈 Impact Analysis" }
        ].map(t => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            style={{
              padding: "10px 20px",
              background: tab === t.id ? "#DC2626" : "white",
              color: tab === t.id ? "white" : "#374151",
              border: "none",
              borderRadius: 8,
              fontWeight: 700,
              cursor: "pointer"
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {/* Calculator Tab */}
      {tab === "calculator" && (
        <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 24 }}>
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: "0 0 20px 0" }}>Shipment Details</h3>
            
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16, marginBottom: 16 }}>
              <div>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Origin Country</label>
                <select value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                  {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
              <div>
                <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Destination Country</label>
                <select value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                  {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
                </select>
              </div>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Commodity Category</label>
              <select value={form.category} onChange={e => setForm({ ...form, category: e.target.value })} style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                {categories.map(c => <option key={c.id} value={c.id}>{c.name}</option>)}
              </select>
            </div>

            <div style={{ marginBottom: 16 }}>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Declared Value (USD)</label>
              <input
                type="number"
                value={form.declaredValue}
                onChange={e => setForm({ ...form, declaredValue: Number(e.target.value) })}
                style={{ width: "100%", padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}
              />
            </div>

            <div style={{ marginBottom: 20 }}>
              <label style={{ display: "flex", alignItems: "center", gap: 8, cursor: "pointer" }}>
                <input
                  type="checkbox"
                  checked={form.isUsmcaCompliant}
                  onChange={e => setForm({ ...form, isUsmcaCompliant: e.target.checked })}
                />
                <span style={{ fontWeight: 600 }}>USMCA Compliant</span>
                <span style={{ fontSize: 12, color: "#6b7280" }}>(meets Regional Value Content requirements)</span>
              </label>
            </div>

            <button
              onClick={calculate}
              disabled={calculating}
              style={{
                width: "100%",
                padding: "12px 24px",
                background: calculating ? "#9ca3af" : "#DC2626",
                color: "white",
                border: "none",
                borderRadius: 8,
                fontWeight: 700,
                cursor: calculating ? "not-allowed" : "pointer",
                fontSize: 16
              }}
            >
              {calculating ? "Calculating..." : "Calculate Tariff"}
            </button>
          </div>

          {/* Results */}
          <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
            <h3 style={{ margin: "0 0 20px 0" }}>Calculation Results</h3>
            
            {!result ? (
              <div style={{ padding: 40, textAlign: "center", color: "#9ca3af" }}>
                Enter shipment details and click Calculate
              </div>
            ) : (
              <div>
                <div style={{ padding: 20, background: result.isUsmcaCompliant ? "#d1fae5" : "#fef3c7", borderRadius: 12, marginBottom: 20 }}>
                  <div style={{ fontSize: 14, color: result.isUsmcaCompliant ? "#065f46" : "#92400e", marginBottom: 4 }}>
                    {result.isUsmcaCompliant ? "✓ USMCA Rate Applied" : "⚠ Standard Rate Applied"}
                  </div>
                  <div style={{ fontSize: 36, fontWeight: 900, color: result.isUsmcaCompliant ? "#10b981" : "#f59e0b" }}>
                    {formatCurrency(result.tariffAmount)}
                  </div>
                  <div style={{ fontSize: 14, color: "#6b7280" }}>
                    {result.appliedRate}% tariff on {formatCurrency(result.declaredValue)}
                  </div>
                </div>

                <div style={{ display: "grid", gap: 12 }}>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                    <span style={{ color: "#6b7280" }}>Standard Rate</span>
                    <span style={{ fontWeight: 700 }}>{result.standardRate}%</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                    <span style={{ color: "#6b7280" }}>USMCA Rate</span>
                    <span style={{ fontWeight: 700, color: "#10b981" }}>{result.usmcaRate}%</span>
                  </div>
                  <div style={{ display: "flex", justifyContent: "space-between", padding: 12, background: "#f9fafb", borderRadius: 8 }}>
                    <span style={{ color: "#6b7280" }}>Tariff Type</span>
                    <span style={{ fontWeight: 700 }}>{result.tariffType}</span>
                  </div>
                  {result.usmcaSavings > 0 && (
                    <div style={{ display: "flex", justifyContent: "space-between", padding: 12, background: "#d1fae5", borderRadius: 8 }}>
                      <span style={{ color: "#065f46" }}>💰 USMCA Savings</span>
                      <span style={{ fontWeight: 700, color: "#10b981" }}>{formatCurrency(result.usmcaSavings)}</span>
                    </div>
                  )}
                  {!result.usmcaEligible && (
                    <div style={{ padding: 12, background: "#fef2f2", borderRadius: 8, color: "#dc2626", fontSize: 13 }}>
                      ⚠ This commodity category is not eligible for USMCA exemption
                    </div>
                  )}
                </div>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Rates Tab */}
      {tab === "rates" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 20 }}>
            <select value={ratesFilter.origin} onChange={e => setRatesFilter({ ...ratesFilter, origin: e.target.value })} style={{ padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
              <option value="">All Origins</option>
              {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <select value={ratesFilter.destination} onChange={e => setRatesFilter({ ...ratesFilter, destination: e.target.value })} style={{ padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
              <option value="">All Destinations</option>
              {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
            </select>
            <button onClick={loadRates} style={{ padding: "10px 20px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Search</button>
          </div>
          
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse" }}>
              <thead>
                <tr style={{ background: "#f9fafb" }}>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700 }}>Route</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700 }}>Category</th>
                  <th style={{ padding: 12, textAlign: "right", fontWeight: 700 }}>Standard</th>
                  <th style={{ padding: 12, textAlign: "right", fontWeight: 700 }}>USMCA</th>
                  <th style={{ padding: 12, textAlign: "left", fontWeight: 700 }}>Type</th>
                </tr>
              </thead>
              <tbody>
                {rates.map((r, i) => (
                  <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                    <td style={{ padding: 12 }}>{r.originCountry} → {r.destCountry}</td>
                    <td style={{ padding: 12 }}>{categories.find(c => c.id === r.commodityCategory)?.name || r.commodityCategory}</td>
                    <td style={{ padding: 12, textAlign: "right", fontWeight: 700 }}>{r.standardRate}%</td>
                    <td style={{ padding: 12, textAlign: "right", fontWeight: 700, color: r.usmcaRate < r.standardRate ? "#10b981" : "#6b7280" }}>{r.usmcaRate}%</td>
                    <td style={{ padding: 12 }}><span style={{ fontSize: 11, padding: "4px 8px", background: "#f3f4f6", borderRadius: 4 }}>{r.tariffType}</span></td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}

      {/* Impact Analysis Tab */}
      {tab === "impact" && (
        <div style={{ background: "white", border: "1px solid #e5e7eb", borderRadius: 12, padding: 24 }}>
          <div style={{ display: "flex", gap: 16, marginBottom: 20, alignItems: "flex-end" }}>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Origin</label>
              <select value={form.origin} onChange={e => setForm({ ...form, origin: e.target.value })} style={{ padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Destination</label>
              <select value={form.destination} onChange={e => setForm({ ...form, destination: e.target.value })} style={{ padding: 10, borderRadius: 6, border: "1px solid #e5e7eb" }}>
                {countries.map(c => <option key={c.code} value={c.code}>{c.name}</option>)}
              </select>
            </div>
            <div>
              <label style={{ fontWeight: 600, display: "block", marginBottom: 4 }}>Sample Value</label>
              <input type="number" value={form.declaredValue} onChange={e => setForm({ ...form, declaredValue: Number(e.target.value) })} style={{ padding: 10, borderRadius: 6, border: "1px solid #e5e7eb", width: 150 }} />
            </div>
            <button onClick={loadAnalysis} style={{ padding: "10px 20px", background: "#DC2626", color: "white", border: "none", borderRadius: 8, fontWeight: 700, cursor: "pointer" }}>Analyze</button>
          </div>

          {analysis && (
            <>
              <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 16, marginBottom: 24 }}>
                <div style={{ padding: 16, background: "#f0fdf4", borderRadius: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: "#065f46" }}>Max USMCA Savings</div>
                  <div style={{ fontSize: 24, fontWeight: 900, color: "#10b981" }}>{formatCurrency(analysis.summary.maxPotentialSavings)}</div>
                </div>
                <div style={{ padding: 16, background: "#f9fafb", borderRadius: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>Avg Standard Rate</div>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>{analysis.summary.avgStandardRate.toFixed(1)}%</div>
                </div>
                <div style={{ padding: 16, background: "#f9fafb", borderRadius: 12, textAlign: "center" }}>
                  <div style={{ fontSize: 13, color: "#6b7280" }}>Categories Analyzed</div>
                  <div style={{ fontSize: 24, fontWeight: 900 }}>{analysis.summary.totalCategories}</div>
                </div>
              </div>

              <table style={{ width: "100%", borderCollapse: "collapse" }}>
                <thead>
                  <tr style={{ background: "#f9fafb" }}>
                    <th style={{ padding: 12, textAlign: "left" }}>Category</th>
                    <th style={{ padding: 12, textAlign: "right" }}>Standard Tariff</th>
                    <th style={{ padding: 12, textAlign: "right" }}>USMCA Tariff</th>
                    <th style={{ padding: 12, textAlign: "right" }}>Potential Savings</th>
                  </tr>
                </thead>
                <tbody>
                  {analysis.analysis.map((a, i) => (
                    <tr key={i} style={{ borderTop: "1px solid #e5e7eb" }}>
                      <td style={{ padding: 12 }}>
                        {a.category}
                        {!a.usmcaEligible && <span style={{ marginLeft: 8, fontSize: 11, color: "#dc2626" }}>No USMCA</span>}
                      </td>
                      <td style={{ padding: 12, textAlign: "right" }}>{formatCurrency(a.standardTariff)}</td>
                      <td style={{ padding: 12, textAlign: "right", color: "#10b981" }}>{formatCurrency(a.usmcaTariff)}</td>
                      <td style={{ padding: 12, textAlign: "right", fontWeight: 700, color: a.potentialSavings > 0 ? "#10b981" : "#6b7280" }}>{formatCurrency(a.potentialSavings)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>
          )}
        </div>
      )}

      {/* Info box */}
      <div style={{ marginTop: 24, padding: 16, background: "#fffbeb", border: "1px solid #fcd34d", borderRadius: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 8 }}>📌 USMCA Compliance Notes</div>
        <ul style={{ margin: 0, paddingLeft: 20, color: "#92400e", fontSize: 14 }}>
          <li>USMCA (United States-Mexico-Canada Agreement) replaced NAFTA in 2020</li>
          <li>Products must meet Regional Value Content (RVC) requirements for tariff exemptions</li>
          <li>Steel & Aluminum are subject to Section 232 tariffs regardless of USMCA status</li>
          <li>Current rates effective as of February 2025 Executive Orders</li>
        </ul>
      </div>
    </div>
  );
}
